/* ---------- Hero: the case beside its picture (built like the study pages' heroes) ---------- */
.hero {
  /* Same overlap as the home hero: cancel the flow space the sticky header reserves. */
  margin-top: calc(-1 * (var(--header-h) + 2 * var(--header-margin)));
  padding-block: calc(var(--header-h) + 2 * var(--header-margin) + var(--sp-12)) var(--sp-16);
  background: var(--surface-alt);
}
.heroGrid {
  display: grid;
  gap: var(--sp-12);
  align-items: center;
}
.heroText > * {
  max-width: 38rem;
}
.heroTitle {
  margin-top: var(--sp-6);
  font-size: clamp(2.4rem, 3.4vw + 1rem, 4.25rem);
  line-height: 1.04;
  letter-spacing: -0.035em;
  text-wrap: balance;
}
.heroLead {
  margin-top: var(--sp-5);
  font-size: var(--fs-lg);
  color: var(--text);
}
.heroActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-4) var(--sp-8);
  margin-top: var(--sp-8);
}
.heroLink {
  font-weight: 600;
  color: var(--text-strong);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.3em;
}
.heroLink:hover {
  color: var(--accent-text);
}
.heroImage {
  border-radius: var(--radius-lg);
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-lg);
}
@media (min-width: 64rem) {
  .heroGrid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--sp-16);
  }
}

/* ---------- At a glance: the headline result beside the facts, on hairlines ---------- */
.glance {
  display: grid;
  gap: var(--sp-10);
}
.result {
  display: grid;
  gap: var(--sp-3);
  margin: 0;
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
/* The one large figure on the page: the same result the case study card leads with. */
.result strong {
  font-family: var(--font-display);
  font-size: clamp(2.5rem, 1.5rem + 3vw, 4rem);
  line-height: 1;
  letter-spacing: -0.035em;
  color: var(--accent-text);
}
.result span {
  max-width: 30rem;
  font-size: var(--fs-lg);
  color: var(--text);
}
.facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--sp-6) var(--sp-8);
  margin: 0;
}
.facts div {
  display: grid;
  /* Top-aligned, so a value beside one that wraps to two lines doesn't drift down. */
  align-content: start;
  gap: var(--sp-1);
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
.facts dt {
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
.facts dd {
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
  color: var(--text-strong);
}
@media (min-width: 64rem) {
  .glance {
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
    gap: var(--sp-16);
    align-items: start;
  }
  /* With no headline result, the facts take the whole row. */
  .factsOnly {
    grid-template-columns: 1fr;
  }
  .factsOnly .facts {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

/* ---------- The case study: one part per h2 (rehypeParts), heading beside its text ---------- */
/* Two classes, so it beats `.prose { max-width }` whichever stylesheet the build puts last:
   at equal weight the production bundle let .prose win and squeezed the parts to 65ch. */
.article:global(.prose) {
  max-width: none;
}
.article > :global(.part) {
  display: grid;
  /* minmax(0, …): a wide table scrolls in its own box instead of stretching the column. */
  grid-template-columns: minmax(0, 1fr);
  gap: var(--sp-4) var(--sp-16);
  margin: 0;
  padding-block: var(--sp-8);
  border-top: 1px solid var(--border-strong);
}
.article :global(.part) > h2 {
  margin: 0;
  font-size: var(--fs-xl);
  text-wrap: balance;
}
.article :global(.part-body) {
  max-width: 46rem;
  margin: 0;
}
.article :global(.part-body) > * + * {
  margin-top: var(--sp-5);
}
.article :global(.part-body) > h3 {
  margin-top: var(--sp-8);
}
.article :global(.part-body) > h3 + * {
  margin-top: var(--sp-3);
}
.download {
  margin: var(--sp-8) 0 0;
}
@media (min-width: 64rem) {
  .article > :global(.part) {
    grid-template-columns: minmax(0, 4fr) minmax(0, 8fr);
  }
  /* A long part keeps its heading in view while its text scrolls past. */
  .article :global(.part) > h2 {
    position: sticky;
    top: calc(var(--header-h) + 2 * var(--header-margin) + var(--sp-6));
    align-self: start;
  }
  /* Text before the first heading sits in the text column too. */
  .article :global(.part--lead) > :global(.part-body) {
    grid-column: 2;
  }
  /* The closing download lines up with the text column: 4 of 12 parts, plus the gap. */
  .download {
    padding-left: calc((100% - var(--sp-16)) / 3 + var(--sp-16));
  }
}
